import { _ } from '/@/locale';
import type { Hybrid } from './hybrid-card';

export type HybridGroup = {
    key: string;
    label: string;
    /** The brand's name, when it has one — the empty state names the brand that came back empty. */
    name?: string;
    hybrids: Hybrid[];
};

/** The parts of a session brand the grouping needs. */
export type HybridBrand = { id?: number; name?: string; codename?: string };

/** The tab key a brand codename buckets into. */
const groupKey = (codename?: string) => `brand:${codename || ''}`;

/**
 * Silo destinations that count as "para silo".
 *
 * "Apto silo" suits either destination, so it belongs here alongside the exclusive one — the
 * same definition the catalogue filter uses (`apps/api/filters.py`, `product-exploration/list`).
 */
const SILO_DESTINATIONS = ['apt', 'exclusive'];

/** One brand's tab, empty until hybrids are pushed into it. */
function emptyGroup(brand: HybridBrand): HybridGroup {
    const name = brand.name;
    return {
        key: groupKey(brand.codename),
        label: name ? _('Recomendados {{brand}}', { brand: name }) : _('Recomendados'),
        name,
        hybrids: [],
    };
}

/**
 * Recommended hybrids split into one group per brand — "Recomendados Dekalb", "Recomendados
 * Preceon" — keyed on the brand codename and ordered by first appearance, which preserves the
 * ranking the API returned.
 *
 * `brands` is the set the ranking was asked for. A brand in it that came back with nothing still
 * gets a group, so the step can offer its tab and say the brand has no hybrids for this field
 * rather than hiding the brand altogether; those empty groups sort after the ones that have
 * hybrids, so the step never opens on an empty tab while a populated one exists.
 *
 * Every hybrid lands in exactly one group: those whose brand carries no name fall into an
 * unbranded bucket keeping the original "Recomendados" label rather than dropping out of the UI.
 * Grouping runs on the unfiltered list so the tabs on offer never change as the silo filter is
 * toggled; `filterSilo` then narrows what each tab shows.
 */
export function groupByBrand(hybrids: Hybrid[], brands: HybridBrand[] = []): HybridGroup[] {
    const groups = new Map<string, HybridGroup>();

    for (const hybrid of hybrids) {
        const key = groupKey(hybrid.brand?.codename);
        let group = groups.get(key);
        if (!group) {
            // The hybrid's own brand names the group: the queried brand carries the same name,
            // and a hybrid from a brand that was not queried still has to land somewhere.
            group = emptyGroup(hybrid.brand || {});
            groups.set(key, group);
        }
        group.hybrids.push(hybrid);
    }

    const empty = brands.filter((brand) => !groups.has(groupKey(brand.codename))).map((brand) => emptyGroup(brand));

    return [...groups.values(), ...empty];
}

/** Whether a hybrid is one the silo filter keeps. */
export function isSiloHybrid(hybrid: Hybrid): boolean {
    return !!hybrid.siloDestination && SILO_DESTINATIONS.includes(hybrid.siloDestination);
}

/**
 * Whether offering the silo filter over this list would tell the user anything.
 *
 * A list that is all silo or all grain filters to either itself or nothing, so the checkbox is
 * hidden. Always pass the *unfiltered* list: computed over the filtered one, the checkbox would
 * disappear the moment it was ticked, with no way left to untick it.
 */
export function hasSiloChoice(hybrids: Hybrid[]): boolean {
    return hybrids.some(isSiloHybrid) && hybrids.some((hybrid) => !isSiloHybrid(hybrid));
}

/**
 * The hybrids a tab shows once the silo filter is applied.
 *
 * Already-selected hybrids survive the filter: they count against the selection limit whether
 * or not they are on screen, so hiding one would strand the user at the limit with nothing
 * visible to deselect.
 */
export function filterSilo(
    hybrids: Hybrid[],
    { siloOnly, keepIds = [] }: { siloOnly: boolean; keepIds?: number[] }
): Hybrid[] {
    if (!siloOnly) return hybrids;
    return hybrids.filter((hybrid) => isSiloHybrid(hybrid) || keepIds.includes(hybrid.id));
}

/** Numbers a list from 1, so a tab never shows the gaps left by grouping or filtering. */
export function rank(hybrids: Hybrid[]): Hybrid[] {
    return hybrids.map((hybrid, index) => ({ ...hybrid, ranking: index + 1 }));
}
